import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import volunteerRequest, {
  setVolunteerSession,
} from 'api/volunteerRequest'
import { getBranchIdFromPathname } from 'utils/helper/static-mode-route'

type VolunteerContextValue = {
  volunteerToken: string
  branchId: string
  apiClient: typeof volunteerRequest
}

const VolunteerContext = createContext<VolunteerContextValue | null>(null)

export function VolunteerContextProvider({
  children,
  branchId: branchIdProp,
}: {
  children: ReactNode
  branchId?: string
}) {
  const [searchParams] = useSearchParams()
  const volunteerToken = searchParams.get('volunteerToken')?.trim() ?? ''
  const branchId = branchIdProp ?? getBranchIdFromPathname()

  useEffect(() => {
    if (volunteerToken && branchId) {
      setVolunteerSession({ volunteerToken, branchId })
    }
    return () => setVolunteerSession(null)
  }, [volunteerToken, branchId])

  const value = useMemo(
    () =>
      volunteerToken && branchId
        ? { volunteerToken, branchId, apiClient: volunteerRequest }
        : null,
    [volunteerToken, branchId]
  )

  if (!value) return null

  return (
    <VolunteerContext.Provider value={value}>
      {children}
    </VolunteerContext.Provider>
  )
}

export function useVolunteerContext(): VolunteerContextValue {
  const ctx = useContext(VolunteerContext)
  if (!ctx) {
    throw new Error('useVolunteerContext must be used within VolunteerContextProvider')
  }
  return ctx
}
